// Buxgalteriya jurnali — xsib-core `ledger` moduli bilan HAQIQIY kontrakt
// (koordinator tasdiqlagan — avvalgi taxmin ESKIRGAN edi, quyida tuzatilgan):
//
// GET /api/ledger/accounts?ownerType&ownerId&type&unit&page&size
//   → {rows:[{id,type,ownerType,ownerId,unit,balance,debtLimit,version,createdAt}],
//      page, size, hasNext}
// GET /api/ledger/accounts/{id}/entries?from&to&page&size   (from/to MAJBURIY)
//   → {rows:[LedgerEntry], page, size, hasNext}
// GET /api/ledger/txns/{id}          → LedgerTxn (entries bilan)
// GET /api/ledger/reconciliation     → ReconciliationReport
// POST /api/ledger/adjust            → 201 LedgerTxn
//      body: {idempotencyKey, lines, reason, reference}   ⚠️ reason MAJBURIY
//
// ⚠️ SXEMALAR JAVA MANBASIDAN TEKSHIRILDI (ledger/api/*.java), taxmin emas:
//   · barcha id'lar `long` — SON, UUID emas
//   · `LedgerEntry` = {id, txnId, accountId, amount, unit, createdAt,
//                      txnKind, txnReference}  ← `at`/`description`/
//                      `balanceAfter` YO'Q
//   · `LedgerTxn`   = {id, kind, idempotencyKey, reference, note,
//                      reversesId, createdAt, entries, replay}
//   · `ReconciliationReport.findings` (`mismatches` EMAS)
//   · `EntryLine`   = {account: AccountRef, amount}  ← `accountId` EMAS
//
// ⚠️ `total` YO'Q — faqat `hasNext` (`shared/api/pageResult.ts`ga qarang).
// ⚠️ Hisobda `code`/`name` YO'Q (eskirgan taxminimda bor edi) — o'rniga
// `type` (hisob turi), `ownerType`/`ownerId` (kimga tegishli — polimorfik:
// abonent, agent va h.k.) va `unit` (pul kodi YOKI boshqa o'lchov birligi,
// masalan trafik hisobi bo'lsa). `balance`/`debtLimit` xom son (minor
// birlik) — `unit` ISO 4217 kodi bo'lsa pul sifatida, aks holda raqam +
// birlik sifatida ko'rsatiladi (`isLikelyCurrency`).
// ⚠️ `LedgerEntry`/`LedgerTxn` ICHKI maydonlari koordinator TOMONIDAN
// ANIQLASHTIRILMAGAN (faqat nomi tasdiqlangan) — bu qism hali ENG ISHONCHLI
// TAXMIN, zod chegarada tekshiradi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { syntheticTotal } from '@/shared/api/pageResult'
import { instantSchema } from '@/shared/api/schemas'
import { newIdempotencyKey } from '@/shared/lib/idempotency'

/**
 * ⚠️ ID'LAR — SON, UUID EMAS. `ledger.api.AccountSummary.id`,
 * `LedgerEntry.id/txnId/accountId`, `LedgerTxn.id` — hammasi `long`.
 * Bu yerda avval `uuidSchema` (satr) turgan va zod hisoblar ro'yxatini HAR
 * safar rad etardi: sahifada jadval o'rniga "bo'lim hali javob bermayapti"
 * ogohlantirishi chiqar, ichida esa aslida BIZNING sxema xatomiz —
 * "Expected string, received number" — ko'rinardi.
 *
 * Ilova ichida id satr sifatida yuritiladi (URL, `rowKey`, kesh kaliti),
 * shuning uchun `String(...)` bilan o'giriladi.
 */
export const ledgerIdSchema = z.union([z.number(), z.string()])

const idSchema = ledgerIdSchema

const ledgerAccountSchema = z.object({
  id: idSchema,
  type: z.string(),
  ownerType: z.string(),
  ownerId: z.string(),
  unit: z.string(),
  balance: z.number(),
  debtLimit: z.number().nullish(),
  version: z.number().nullish(),
  createdAt: instantSchema.nullish(),
})

export interface LedgerAccount {
  id: string
  type: string
  ownerType: string
  ownerId: string
  unit: string
  balance: number
  debtLimit: number | null
  version: number | null
  createdAt: string | null
}

function toLedgerAccount(raw: z.infer<typeof ledgerAccountSchema>): LedgerAccount {
  return {
    id: String(raw.id),
    type: raw.type,
    ownerType: raw.ownerType,
    ownerId: raw.ownerId,
    unit: raw.unit,
    balance: raw.balance,
    debtLimit: raw.debtLimit ?? null,
    version: raw.version ?? null,
    createdAt: raw.createdAt ?? null,
  }
}

export const hasNextPageSchema = <Row extends z.ZodTypeAny>(row: Row) =>
  z.object({ rows: z.array(row), page: z.number(), size: z.number(), hasNext: z.boolean() })

const ledgerAccountPageSchema = hasNextPageSchema(ledgerAccountSchema)

/**
 * `ledger.journal.enums.OwnerType` ning NUSXASI — yopiq ro'yxat.
 *
 * ⚠️ Serverdan olinmaydi va olinishi ham kerak emas: bu enum
 * `account.owner_type` ustunining o'zi (`OwnerType` javadoc'i). Ro'yxat
 * FILTR uchun kerak — `GET /api/ledger/accounts` `ownerType` ni
 * `OwnerType` tipida qabul qiladi, ya'ni ro'yxatdan tashqari qiymat
 * yuborilsa server 400 beradi.
 */
export const LEDGER_OWNER_TYPES = ['AGENT', 'RESELLER', 'SUBSCRIBER', 'PSP', 'TENANT', 'ORG_NODE'] as const

/**
 * Ega turiga mos TANLAGICH — yoki `null`, tanlagich yo'q.
 *
 * ⚠️ `PSP` va `TENANT` ATAYLAB ro'yxatda yo'q: ularning reyestri panelda
 * umuman yo'q va bo'sh tanlagich «bunday ega yo'q» degan yolg'on
 * ko'rsatardi. Ular uchun qo'lda kiritish YAGONA to'g'ri yo'l.
 *
 * ⚠️ `RESELLER` ham `AGENT` tanlagichiga tushadi — ikkalasi bitta
 * daraxtning tugunlari (`org_node.kind`), ya'ni ro'yxat ham bitta.
 */
export function ownerPickerKind(ownerType: string | undefined): 'AGENT' | 'SUBSCRIBER' | null {
  if (ownerType === 'AGENT' || ownerType === 'RESELLER') return 'AGENT'
  if (ownerType === 'SUBSCRIBER') return 'SUBSCRIBER'
  return null
}

/**
 * `ledger.journal.enums.AccountType` ning NUSXASI — yopiq ro'yxat,
 * `ownerType` bilan bir xil sabab (`type` ham enum sifatida bog'lanadi).
 */
export const LEDGER_ACCOUNT_TYPES = [
  'AGENT_DEPOSIT',
  'SUBSCRIBER_BALANCE',
  'SUBSCRIBER_TRAFFIC',
  'PSP_SETTLEMENT',
  'REVENUE',
  'COMMISSION_EXPENSE',
  'CASH_IN_TRANSIT',
  'WRITE_OFF',
  'TRAFFIC_POOL',
  'BANK',
  'BRANCH_CASH',
  'CASH_TRANSFER_IN_TRANSIT',
  'CASH_OVER_SHORT',
  'CASH_OTHER_INCOME',
  'CASH_EXPENSE',
  'DISCOUNT',
] as const

/**
 * `owner_id` UUID shakli.
 *
 * ⚠️ NEGA KERAK: kontrollerda `@RequestParam UUID ownerId` — Spring
 * yaroqsiz satrni O'ZI rad etadi va 400 qaytaradi. Ya'ni qidiruv
 * maydoniga «abc» yozilishi butun jadvalni xato holatiga tushirardi.
 * Panel bunday qiymatni SERVERGA UMUMAN YUBORMAYDI va sababini
 * chaqiruvchiga qaytaradi.
 */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function looksLikeOwnerId(value: string): boolean {
  return UUID_SHAPE.test(value.trim())
}

export interface LedgerAccountsFilter {
  ownerType?: string
  ownerId?: string
  type?: string
  unit?: string
}

export interface LedgerAccountsResult {
  items: LedgerAccount[]
  hasNext: boolean
  /** `DataTable`/antd `Pagination` uchun sun'iy son — `pageResult.ts`ga qarang */
  total: number
}

export async function fetchLedgerAccounts(
  filter: LedgerAccountsFilter,
  page1Based: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<LedgerAccountsResult> {
  const response = await api.get<unknown>('/api/ledger/accounts', {
    ...(signal ? { signal } : {}),
    params: {
      ...(filter.ownerType ? { ownerType: filter.ownerType } : {}),
      ...(filter.ownerId ? { ownerId: filter.ownerId } : {}),
      ...(filter.type ? { type: filter.type } : {}),
      ...(filter.unit ? { unit: filter.unit } : {}),
      page: page1Based - 1,
      size: pageSize,
    },
  })
  const parsed = ledgerAccountPageSchema.parse(response.data)
  return {
    items: parsed.rows.map(toLedgerAccount),
    hasNext: parsed.hasNext,
    total: syntheticTotal(page1Based, pageSize, parsed.rows.length, parsed.hasNext),
  }
}

/**
 * `ledger.api.LedgerEntry` — HAQIQIY maydonlar.
 *
 * ⚠️ Avvalgi taxmin (`at`, `description`, `balanceAfter`) NOTO'G'RI edi:
 *   · vaqt maydoni `createdAt` deb ataladi, `at` emas
 *   · izoh maydoni umuman yo'q — o'rniga `txnKind` (biznes hodisasi turi)
 *     va `txnReference` (PSP txn id, chek raqami) keladi, ular ataylab
 *     yozuvga qo'shilgan ("nima uchun" savoliga javob bersin deb)
 *   · YUGURUVCHI QOLDIQ (`balanceAfter`) SERVERDA YO'Q va uni mijozda
 *     hisoblab bo'lmaydi: ko'chirma sahifalab keladi, ya'ni bir sahifada
 *     boshlang'ich qoldiq noma'lum. Uni "hisoblab" ko'rsatish son o'ylab
 *     topish bo'lardi — shuning uchun ustunning o'zi olib tashlandi.
 *
 * `unit` har yozuvda bor — summani hisob birligiga tayanmasdan
 * ko'rsatish mumkin (trafik hisobi ham bo'lishi mumkin).
 */
const ledgerEntrySchema = z.object({
  id: idSchema,
  txnId: idSchema,
  accountId: idSchema,
  /** Ishorali son (minor birlik): musbat = kredit, manfiy = debit */
  amount: z.number(),
  unit: z.string(),
  createdAt: instantSchema,
  txnKind: z.string().nullish(),
  txnReference: z.string().nullish(),
})

export interface LedgerEntry {
  id: string
  txnId: string
  accountId: string
  amount: number
  unit: string
  createdAt: string
  txnKind: string | null
  txnReference: string | null
}

function toLedgerEntry(raw: z.infer<typeof ledgerEntrySchema>): LedgerEntry {
  return {
    id: String(raw.id),
    txnId: String(raw.txnId),
    accountId: String(raw.accountId),
    amount: raw.amount,
    unit: raw.unit,
    createdAt: raw.createdAt,
    txnKind: raw.txnKind ?? null,
    txnReference: raw.txnReference ?? null,
  }
}

const ledgerEntryPageSchema = hasNextPageSchema(ledgerEntrySchema)

export interface LedgerEntriesResult {
  items: LedgerEntry[]
  hasNext: boolean
  total: number
}

/** Sana oralig'i MAJBURIY — hisob tarixi cheksiz o'sishi mumkin */
export async function fetchLedgerEntries(
  accountId: string,
  from: string,
  to: string,
  page1Based: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<LedgerEntriesResult> {
  const response = await api.get<unknown>(`/api/ledger/accounts/${accountId}/entries`, {
    ...(signal ? { signal } : {}),
    params: { from, to, page: page1Based - 1, size: pageSize },
  })
  const parsed = ledgerEntryPageSchema.parse(response.data)
  return {
    items: parsed.rows.map(toLedgerEntry),
    hasNext: parsed.hasNext,
    total: syntheticTotal(page1Based, pageSize, parsed.rows.length, parsed.hasNext),
  }
}

/**
 * `ledger.api.LedgerTxn` — vaqt maydoni `createdAt`, izoh maydoni `note`
 * (`reason` EMAS: `reason` faqat SO'ROVDA bor, javobda u `note` ga
 * aylanadi). `replay=true` — bu chaqiruv hech narsa yozmadi, mavjud
 * tranzaksiya qaytarildi (idempotentlik).
 */
const ledgerTxnSchema = z.object({
  id: idSchema,
  createdAt: instantSchema,
  kind: z.string().nullish(),
  note: z.string().nullish(),
  reference: z.string().nullish(),
  replay: z.boolean().nullish(),
  entries: z.array(ledgerEntrySchema).default([]),
})

export interface LedgerTxn {
  id: string
  createdAt: string
  kind: string | null
  note: string | null
  reference: string | null
  replay: boolean
  entries: LedgerEntry[]
}

function toLedgerTxn(raw: z.infer<typeof ledgerTxnSchema>): LedgerTxn {
  return {
    id: String(raw.id),
    createdAt: raw.createdAt,
    kind: raw.kind ?? null,
    note: raw.note ?? null,
    reference: raw.reference ?? null,
    replay: raw.replay ?? false,
    entries: raw.entries.map(toLedgerEntry),
  }
}

export async function fetchLedgerTxn(id: string, signal?: AbortSignal): Promise<LedgerTxn> {
  const response = await api.get<unknown>(`/api/ledger/txns/${id}`, {
    ...(signal ? { signal } : {}),
  })
  return toLedgerTxn(ledgerTxnSchema.parse(response.data))
}

/**
 * `ledger.api.ReconciliationReport`.
 *
 * ⚠️ Nomuvofiqliklar ro'yxati `findings` deb ataladi, `mismatches` emas.
 * Bu farq XAVFLI edi: eski sxemada `mismatches` ga `.default([])` qo'yilgan,
 * ya'ni maydon topilmaganda zod jimgina BO'SH RO'YXAT yasardi va panel
 * har doim "0 nomuvofiqlik" deb ko'rsatardi — pul jurnalida bu eng yomon
 * turdagi yolg'on.
 *
 * Har topilma `{check, subject, expected, actual}`: `subject` — nima
 * haqida (hisob murojaati, txn id yoki birlik), `check` — qaysi tekshiruv.
 * ALOHIDA `unit` maydoni YO'Q, shuning uchun summalar xom son sifatida
 * ko'rsatiladi (topilma trafik hisobiga ham tegishli bo'lishi mumkin).
 */
const reconciliationSchema = z.object({
  checkedAt: instantSchema.nullish(),
  accountsChecked: z.number(),
  entriesChecked: z.number().nullish(),
  findings: z
    .array(
      z.object({
        check: z.string(),
        subject: z.string(),
        expected: z.number(),
        actual: z.number(),
      }),
    )
    .default([]),
  truncated: z.boolean().nullish(),
  clean: z.boolean().nullish(),
})

export interface LedgerFinding {
  check: string
  subject: string
  expected: number
  actual: number
}

export interface LedgerReconciliation {
  checkedAt: string | null
  accountsChecked: number
  entriesChecked: number | null
  findings: readonly LedgerFinding[]
  /** Ro'yxat chegaradan oshib qisqartirildimi */
  truncated: boolean
  clean: boolean
}

export async function fetchLedgerReconciliation(
  signal?: AbortSignal,
): Promise<LedgerReconciliation> {
  const response = await api.get<unknown>('/api/ledger/reconciliation', {
    ...(signal ? { signal } : {}),
  })
  const raw = reconciliationSchema.parse(response.data)
  return {
    checkedAt: raw.checkedAt ?? null,
    accountsChecked: raw.accountsChecked,
    entriesChecked: raw.entriesChecked ?? null,
    findings: raw.findings,
    truncated: raw.truncated ?? false,
    // `clean` serverdan keladi (`isClean()`), lekin u yo'q bo'lsa ham
    // topilmalar ro'yxatidan aniqlash mumkin — taxmin emas, ta'rif
    clean: raw.clean ?? raw.findings.length === 0,
  }
}

/**
 * ⚠️ Yozuv hisobga ID bilan emas, MUROJAAT bilan bog'lanadi:
 * `ledger.api.EntryLine` = `{account: {type, ownerType, ownerId, unit}, amount}`.
 * Ilgari bu yerda `{accountId, amount}` yuborilardi — server buni umuman
 * bog'lay olmasdi.
 */
export interface LedgerAccountRef {
  type: string
  ownerType: string
  ownerId: string
  unit: string
}

export interface LedgerAdjustLine {
  account: LedgerAccountRef
  /** Ishorali son (minor birlik) — musbat = kredit, manfiy = debit */
  amount: number
}

export interface LedgerAdjustRequest {
  /**
   * ⚠️ IKKI TOMONLAMA YOZUV: yig'indi HAR BIRLIK BO'YICHA NOL bo'lishi
   * shart, aks holda server `UnbalancedTxnException` bilan rad etadi.
   * Ya'ni bitta hisobni "shunchaki tuzatish" mumkin emas — qarshi hisob
   * ham ko'rsatilishi kerak (odatda `WRITE_OFF` yoki `REVENUE`).
   */
  lines: readonly LedgerAdjustLine[]
  /** MAJBURIY — bo'sh sabab bilan yuborilmaydi (`LedgerPage` da tekshiriladi) */
  reason: string
  reference?: string
}

/** `AccountSummary` dan `EntryLine.account` uchun murojaat yasaydi */
export function accountRefOf(account: LedgerAccount): LedgerAccountRef {
  return {
    type: account.type,
    ownerType: account.ownerType,
    ownerId: account.ownerId,
    unit: account.unit,
  }
}

/** Trafik hisoblarining birligi — `AccountType.BYTE_UNIT` bilan bir xil */
export const BYTE_UNIT = 'BYTE'

/**
 * Qo'lda tuzatishning QARSHI hisobi.
 *
 * <h2>⚠️ NEGA U UMUMAN KERAK — VA NEGA PANEL UNI O'ZI TANLAYDI</h2>
 * Jurnal ikki tomonlama: yig'indi har birlik bo'yicha NOL bo'lishi shart
 * (`LedgerAdjustRequest`). Panel esa 2026-08-15 gacha BITTA satr
 * yuborardi va server har safar rad etardi:
 *
 * <pre>
 *   POST /api/ledger/adjust  →  400
 *   "Ikki tomonlama yozuv kamida ikki qatordan iborat, berilgani: 1"
 * </pre>
 *
 * Ya'ni "Qo'lda tuzatish" tugmasi hech qachon ishlamagan (jonli
 * o'lchandi, `admin.x-sib.uz`, 2026-08-16).
 *
 * ⚠️ Qarshi hisobni OPERATORDAN so'ramaymiz. Tuzatishning ma'nosi
 * ta'rifi bo'yicha bitta: pul hech qayerdan kelmadi va hech qayerga
 * ketmadi — u KECHIRILDI yoki qo'shib berildi. `WRITE_OFF` aynan shu
 * uchun bor ("Pulsiz tuzatish: sovg'a, kompensatsiya. Kontr-hisob" —
 * `AccountType`). Operatorga hisoblar ro'yxatini ko'rsatish uni
 * buxgalteriya qaroriga majburlardi va noto'g'ri tanlov JIMGINA
 * daromadni buzardi (provodka o'zgarmas).
 *
 * ⚠️ Trafik hisobida (`BYTE`) `WRITE_OFF` YARAMAYDI — u pul hisobi va
 * `AccountType.accepts` uni rad etadi. Bayt uchun qarshi hisob —
 * tenantning trafik zaxirasi, ya'ni abonentga berilgan trafik aynan
 * o'sha zaxiradan chiqadi (`docs/02-LEDGER.md` §7 dagi misol).
 *
 * @param tenantId `/api/me` dagi `tenantId` — u `ledger.account.owner_id`
 *                 bilan AYNI qiymat (`owner_type = 'TENANT'` qatorlarida
 *                 jonli tekshirildi)
 */
export function counterAccountRef(account: LedgerAccount, tenantId: string): LedgerAccountRef {
  return {
    type: account.unit === BYTE_UNIT ? 'TRAFFIC_POOL' : 'WRITE_OFF',
    ownerType: 'TENANT',
    ownerId: tenantId,
    unit: account.unit,
  }
}

/**
 * Muvozanatli tuzatish satrlari: nishon hisob + qarshi hisob.
 *
 * ⚠️ Yig'indi ATAYLAB shu yerda nolga keltiriladi — chaqiruvchida emas.
 * Ikki joyda hisoblansa, biri bir kuni ishorani unutardi va xato
 * SERVERDA, 400 bo'lib chiqardi (aynan shu bo'lgan).
 */
export function balancedAdjustLines(
  account: LedgerAccount,
  tenantId: string,
  amount: number,
): LedgerAdjustLine[] {
  return [
    { account: accountRefOf(account), amount },
    { account: counterAccountRef(account, tenantId), amount: -amount },
  ]
}

/**
 * Qo'lda tuzatish — XAVFLI AMAL, `ledger.adjust` huquqi kerak.
 *
 * ⚠️ QAYTARIB BO'LMAYDI: bu pul jurnaliga yangi yozuv qo'shadi (o'chirish
 * emas). Xato tuzatish faqat TESKARI yozuv bilan (yana bir `adjust`,
 * teskari ishora bilan) — `LedgerPage` buni operatorga aniq aytadi.
 */
export async function adjustLedger(request: LedgerAdjustRequest): Promise<LedgerTxn> {
  // Muvaffaqiyat — 201 va yaratilgan `LedgerTxn`. Bu `OperationResult` EMAS:
  // oraliq holat (PARTIAL) yo'q, yo yozildi (201), yo butunlay rad etildi
  // (4xx/5xx) — chaqiruvchi (`LedgerPage`) xatoni oddiy `catch` bilan ushlaydi.
  const response = await api.post<unknown>('/api/ledger/adjust', {
    // ⚠️ `crypto.randomUUID()` EMAS: u XAVFSIZ KONTEKST talab qiladi va
    // panel oddiy HTTP ustidan (IP bilan) ochilganda `undefined` bo'ladi.
    // Jonli o'lchandi (`http://10.10.10.12:18081`, 2026-08-16): tugma
    // bosilganda `TypeError: crypto.randomUUID is not a function` chiqib,
    // so'rov UMUMAN yuborilmasdi va operator "kutilmagan xato" ko'rardi.
    // `shared/lib/idempotency` allaqachon shu tuzoqni hisobga oladi.
    idempotencyKey: newIdempotencyKey('ledger-adjust'),
    lines: request.lines,
    reason: request.reason,
    ...(request.reference ? { reference: request.reference } : {}),
  })
  return toLedgerTxn(ledgerTxnSchema.parse(response.data))
}
